import React, { useEffect, useState } from "react";
import { fetchUserResults } from "../../api";
import { Link } from "react-router-dom";
import toast, { Toaster } from 'react-hot-toast';
import { 
  ChartBarIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  TrophyIcon,
  DocumentCheckIcon
} from '@heroicons/react/24/outline';
import * as XLSX from "xlsx";

const UserResults = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredResults, setFilteredResults] = useState([]);

  useEffect(() => {
    const getResults = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log('🔍 Fetching user results...');
        const data = await fetchUserResults();
        console.log('📥 Fetched results:', data);
        
        if (data && data.error) {
          console.error('❌ API Error:', data.error);
          toast.error(data.error || "Failed to fetch results");
          setResults([]);
          setError(data.error);
          return;
        }
        
        if (Array.isArray(data)) {
          console.log(`✅ Successfully loaded ${data.length} results`);
          setResults(data);
          setFilteredResults(data);
          toast.success(`Loaded ${data.length} result${data.length !== 1 ? 's' : ''}`);
        } else {
          console.error('❌ Expected array, got:', typeof data);
          setResults([]);
          setFilteredResults([]);
          setError("Invalid response format");
          toast.error("Invalid response format");
        }
        
      } catch (error) {
        console.error("❌ Error fetching results:", error);
        toast.error(error.message || "Failed to fetch results");
        setResults([]);
        setFilteredResults([]);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    getResults();
  }, []);

  useEffect(() => {
    const filtered = results.filter(result => 
      result.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.exam_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      result.uid?.toString().includes(searchTerm)
    );
    setFilteredResults(filtered);
  }, [searchTerm, results]);

  const exportToExcel = () => {
    try {
      const exportData = filteredResults.map(r => ({
        'Report ID': r.report_id,
        'User ID': r.uid,
        'Username': r.username,
        'Name': r.name,
        'Parents Number': r.parents_number,
        'Exam Name': r.exam_name,
        'Total Questions': r.total_questions,
        'Total Answered': r.total_answered,
        'Score': r.score ? r.score.toFixed(2) + '%' : '0.00%'
      }));
      
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Results");
      XLSX.writeFile(workbook, `exam_results_${new Date().toLocaleDateString()}.xlsx`);
      toast.success('Excel file downloaded successfully!');
    } catch (error) {
      toast.error('Failed to export to Excel');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'bg-green-100 text-green-800';
    if (score >= 60) return 'bg-blue-100 text-blue-800';
    if (score >= 40) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const averageScore = results.length > 0 
    ? (results.reduce((sum, r) => sum + (r.score || 0), 0) / results.length).toFixed(2)
    : 0;

  const highScore = results.length > 0 
    ? Math.max(...results.map(r => r.score || 0)).toFixed(2)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex justify-center items-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <Toaster position="top-right" />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                <ChartBarIcon className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Exam Results</h1>
                <p className="text-sm text-gray-500">
                  Total {filteredResults.length} result{filteredResults.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            {results.length > 0 && (
              <button
                onClick={exportToExcel}
                className="flex items-center space-x-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 transition-all shadow-md"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
                <span>Export to Excel</span>
              </button>
            )}
          </div>

          {/* Search Bar */}
          {results.length > 0 && (
            <div className="relative mt-4">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, username, exam name, or user ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              />
            </div>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">
                  <strong>Error:</strong> {error}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        {results.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Total Results</p>
                  <p className="text-3xl font-bold text-gray-800 mt-2">{results.length}</p>
                </div>
                <DocumentCheckIcon className="h-12 w-12 text-blue-400" />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Average Score</p>
                  <p className="text-3xl font-bold text-indigo-600 mt-2">{averageScore}%</p>
                </div>
                <ChartBarIcon className="h-12 w-12 text-indigo-400" />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">Highest Score</p>
                  <p className="text-3xl font-bold text-green-600 mt-2">{highScore}%</p>
                </div>
                <TrophyIcon className="h-12 w-12 text-green-400" />
              </div>
            </div>
          </div>
        )}

        {/* Table Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">ID</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">User</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Username</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Parents No.</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Exam Name</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Questions</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Answered</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold uppercase tracking-wider">Score</th>
                  <th className="px-4 py-4 text-center text-xs font-semibold uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredResults.length > 0 ? (
                  filteredResults.map((result, index) => (
                    <tr key={result.report_id} className={`hover:bg-gray-50 transition-colors ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <span className="text-sm font-semibold text-blue-600">
                            {result.uid}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-800">
                          {result.name || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">
                          {result.username || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">
                          {result.parents_number || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-800">
                          {result.exam_name || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {result.total_questions}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {result.total_answered}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                          getScoreColor(result.score || 0)
                        }`}>
                          {result.score ? result.score.toFixed(2) : '0.00'}%
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <Link
                          to={`/admin/result/${result.uid}`}
                          className="inline-flex items-center space-x-1 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-all font-medium text-sm border border-blue-200"
                        >
                          <EyeIcon className="h-4 w-4" />
                          <span>View</span>
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center space-y-3">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <ChartBarIcon className="h-8 w-8 text-gray-400" />
                        </div>
                        <p className="text-gray-500 font-medium">
                          {searchTerm ? 'No results found matching your search' : 'No results found'}
                        </p>
                        <p className="text-sm text-gray-400">
                          {searchTerm ? '' : 'Students haven\'t submitted any exams yet'}
                        </p>
                        {searchTerm && (
                          <button
                            onClick={() => setSearchTerm('')}
                            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                          >
                            Clear search
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          {filteredResults.length > 0 && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>
                  Showing <span className="font-semibold text-gray-800">{filteredResults.length}</span> of{' '}
                  <span className="font-semibold text-gray-800">{results.length}</span> results
                </span>
                <span className="text-xs text-gray-500">
                  Last updated: {new Date().toLocaleDateString()}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserResults;
